/**
 * The replay scrubber: a time ruler, a track that fills as playback runs, one
 * marker per visible event, and a playhead that moves.
 *
 * The track is real elapsed time, so markers sit where the events actually
 * happened and a long quiet stretch looks quiet. Click or drag anywhere on
 * the track to scrub; click a marker to jump exactly to that event.
 *
 * `cursorMs` is milliseconds since the first visible event. It is optional:
 * without it the playhead sits on the selected event, which is what the
 * component's own tests render.
 */
import { useCallback, useRef, type PointerEvent as ReactPointerEvent } from 'react';
import type { ReplayEvent } from '../../api/replay';
import { cn } from '@/lib/utils';
import { EVENT_STYLES, toneClass } from './eventStyles';
import { formatClock } from './time';

interface Props {
  events: ReplayEvent[];
  selectedEventId: string | null;
  onSelectEvent: (id: string) => void;
  /** Playhead position, ms since the first visible event. */
  cursorMs?: number;
  /** Called with a new cursor position (ms) when the user scrubs. */
  onScrub?: (ms: number) => void;
}

const TICKS = 5;

export function ReplayTimeline({
  events,
  selectedEventId,
  onSelectEvent,
  cursorMs,
  onScrub,
}: Props) {
  const trackRef = useRef<HTMLDivElement | null>(null);

  const scrubTo = useCallback(
    (clientX: number, span: number) => {
      const el = trackRef.current;
      if (!el || !onScrub) return;
      const rect = el.getBoundingClientRect();
      if (rect.width === 0) return;
      const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      onScrub(ratio * span);
    },
    [onScrub],
  );

  if (events.length === 0) {
    return (
      <div
        data-testid="replay-timeline-empty"
        className="grid h-20 place-items-center rounded-control border border-dashed border-line text-meta text-ink-faint"
      >
        No events match the current filters.
      </div>
    );
  }

  const timestamps = events.map((e) => Date.parse(e.timestamp));
  const tMin = Math.min(...timestamps);
  const tMax = Math.max(...timestamps);
  const span = Math.max(0, tMax - tMin);

  const selected = events.find((e) => e.id === selectedEventId) ?? null;
  const cursor =
    cursorMs !== undefined
      ? Math.min(span, Math.max(0, cursorMs))
      : selected
        ? Date.parse(selected.timestamp) - tMin
        : 0;
  const pct = (ms: number) => (span === 0 ? 0 : (ms / span) * 100);

  function onPointerDown(e: ReactPointerEvent<HTMLDivElement>) {
    if (!onScrub) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    scrubTo(e.clientX, span);
  }
  function onPointerMove(e: ReactPointerEvent<HTMLDivElement>) {
    if (!onScrub || !e.currentTarget.hasPointerCapture(e.pointerId)) return;
    scrubTo(e.clientX, span);
  }
  function onPointerUp(e: ReactPointerEvent<HTMLDivElement>) {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  }

  const ticks = Array.from({ length: TICKS }, (_, i) => (span * i) / (TICKS - 1));

  return (
    <div data-testid="replay-timeline" className="px-2.5">
      {/* Ruler */}
      <div className="relative mb-1.5 h-4" aria-hidden>
        {ticks.map((t, i) => (
          <span
            key={i}
            style={{ left: `${pct(t)}%` }}
            className={cn(
              'absolute top-0 text-[11px] text-ink-faint tabular-nums',
              i === 0 ? '' : i === TICKS - 1 ? '-translate-x-full' : '-translate-x-1/2',
            )}
          >
            {formatClock(t)}
          </span>
        ))}
      </div>

      {/* Track. Generous vertical padding so the hit area is comfortable
          without making the bar itself heavy. */}
      <div
        ref={trackRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        className={cn('relative h-7 touch-none select-none', onScrub && 'cursor-pointer')}
      >
        <div className="absolute inset-x-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-muted" />
        <div
          className="absolute top-1/2 left-0 h-1.5 -translate-y-1/2 rounded-full bg-field/70"
          style={{ width: `${pct(cursor)}%` }}
          aria-hidden
        />

        {events.map((event) => {
          const style = EVENT_STYLES[event.type];
          const at = Date.parse(event.timestamp) - tMin;
          const isSelected = event.id === selectedEventId;
          const played = at <= cursor + 1;
          return (
            <button
              key={event.id}
              type="button"
              data-testid={`replay-dot-${event.id}`}
              data-event-type={event.type}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => onSelectEvent(event.id)}
              title={`${style.label} · ${formatClock(at)}`}
              aria-label={`${style.label} at ${formatClock(at)}`}
              style={{ left: `${pct(at)}%` }}
              className={cn(
                'absolute top-1/2 grid h-7 w-5 -translate-x-1/2 -translate-y-1/2 place-items-center',
                toneClass(event.type),
              )}
            >
              <span
                className={cn(
                  'block rounded-full border-2 border-card bg-ev transition-all',
                  isSelected ? 'size-4 ring-2 ring-ev/40' : 'size-3',
                  played ? 'opacity-100' : 'opacity-45',
                )}
              />
            </button>
          );
        })}

        {selectedEventId !== null ? (
          <div
            data-testid="replay-timeline-scrubber"
            aria-hidden
            style={{ left: `${pct(cursor)}%` }}
            className="pointer-events-none absolute inset-y-0 w-px -translate-x-1/2 bg-field"
          >
            <span className="absolute -top-0.5 left-1/2 size-2 -translate-x-1/2 rotate-45 rounded-[1px] bg-field" />
          </div>
        ) : null}
      </div>
    </div>
  );
}
